import { useState, useEffect, useRef, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Check, ArrowRight, ArrowLeft, Clock, AlertTriangle, Zap } from 'lucide-react';

interface DraftOrderEntry {
  id: string;
  team_id: string;
  round: number;
  pick_number: number;
  round_pick: number;
}

interface ActiveSabotage {
  id: string;
  target_team_id: string;
  effect_type: string;
  status: string;
  applied_at_pick: number;
  expires_at_pick: number | null;
}

interface DraftOrderStripProps {
  draftOrder: DraftOrderEntry[];
  teams: { id: string; name: string; owner: string }[];
  currentPick: number;
  currentRound: number;
  totalRounds: number;
  timeRemaining: number;
  totalTime: number;
  isPaused: boolean;
  isSlowDraft: boolean;
  overnightPauseActive: boolean;
  pauseEndHour: number;
  activeSabotages: ActiveSabotage[];
  skippedPickNumbers: Set<number>;
  myTeamId?: string;
  /** Previous round's team order for detecting reversals */
  prevOrderRef?: React.MutableRefObject<string[]>;
}

/** Pizza-tracker stage index for slow drafts */
function getStageIndex(timeRemaining: number, totalTime: number): number {
  if (totalTime <= 0) return 0;
  if (timeRemaining <= 900) return 3;   // Expiring (15 min)
  if (timeRemaining <= 3600) return 2;  // Final Hour
  if (timeRemaining / totalTime <= 0.5) return 1; // Halfway
  return 0; // Pick Started
}

const STAGE_ICONS = ['🕐', '⏳', '⚠️', '🔴'];

const DraftOrderStrip = ({
  draftOrder,
  teams,
  currentPick,
  currentRound,
  totalRounds,
  timeRemaining,
  totalTime,
  isPaused,
  isSlowDraft,
  overnightPauseActive,
  pauseEndHour,
  activeSabotages,
  skippedPickNumbers,
  myTeamId,
}: DraftOrderStripProps) => {
  const [reversalFlash, setReversalFlash] = useState(false);
  const prevRoundOrderRef = useRef<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Get entries for the current round
  const currentRoundEntries = useMemo(() =>
    draftOrder
      .filter(o => o.round === currentRound)
      .sort((a, b) => a.pick_number - b.pick_number),
    [draftOrder, currentRound]
  );

  // Detect snake direction: odd rounds go left→right, even go right→left
  const isReversedRound = currentRound % 2 === 0;

  // Detect Snake Reversal by comparing current round order to previous snapshot
  useEffect(() => {
    const currentTeamOrder = currentRoundEntries.map(e => e.team_id);
    const prev = prevRoundOrderRef.current;

    if (prev.length > 0 && currentTeamOrder.length === prev.length) {
      // Check if order changed (reversal happened)
      const changed = currentTeamOrder.some((id, i) => id !== prev[i]);
      if (changed) {
        setReversalFlash(true);
        setTimeout(() => setReversalFlash(false), 3000);
      }
    }

    prevRoundOrderRef.current = currentTeamOrder;
  }, [currentRoundEntries]);

  // Auto-scroll to active team box
  useEffect(() => {
    if (!scrollRef.current) return;
    const activeEl = scrollRef.current.querySelector('[data-active="true"]');
    if (activeEl) {
      activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [currentPick]);

  // Timer formatting
  const formatTime = (seconds: number) => {
    if (isSlowDraft) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      if (hours > 0) return `${hours}h ${mins.toString().padStart(2, '0')}m`;
      if (mins > 0) {
        const secs = seconds % 60;
        return `${mins}m ${secs.toString().padStart(2, '0')}s`;
      }
      return `${seconds}s`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPauseEnd = () => {
    const hour = pauseEndHour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${ampm}`;
  };

  // Timer urgency
  const isLowTime = isSlowDraft ? timeRemaining <= 3600 : timeRemaining <= 15;
  const isCritical = isSlowDraft ? timeRemaining <= 900 : timeRemaining <= 5;
  const stageIdx = isSlowDraft ? getStageIndex(timeRemaining, totalTime) : -1;

  // Check sabotages for a team
  const getTeamSabotages = (teamId: string) => {
    const sabs = activeSabotages.filter(s =>
      s.target_team_id === teamId &&
      (s.status === 'active' || s.status === 'pending') &&
      (s.expires_at_pick === null || s.expires_at_pick >= currentPick)
    );
    return {
      hasPickSkip: sabs.some(s => s.effect_type === 'pick_skip'),
      hasGuessWho: sabs.some(s => s.effect_type === 'guess_who'),
      hasHalfTime: sabs.some(s => s.effect_type === 'timer_reduction'),
    };
  };

  if (currentRoundEntries.length === 0) return null;

  return (
    <div className="shrink-0 relative">
      {/* Snake Reversal flash banner */}
      {reversalFlash && (
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <Badge className="bg-purple-500/90 text-white border-purple-400 shadow-lg shadow-purple-500/30 gap-1 text-xs">
            🐍 Reversal!
          </Badge>
        </div>
      )}

      <div className="rounded-lg border border-border/50 bg-muted/20 px-4 py-3">
        {/* Round info + direction */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2.5">
            <span className="text-xs font-semibold text-muted-foreground">
              Round {currentRound}/{totalRounds}
            </span>
            <div className="flex items-center gap-1 text-muted-foreground/60">
              {isReversedRound ? (
                <>
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span className="text-[11px]">Snake ←</span>
                </>
              ) : (
                <>
                  <span className="text-[11px]">Snake →</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </div>
          </div>
          <span className="text-xs text-muted-foreground">
            Pick {currentPick}/{draftOrder.length}
          </span>
        </div>

        {/* Team boxes */}
        <div ref={scrollRef} className="flex gap-1.5 overflow-x-auto pb-1">
          {currentRoundEntries.map((entry) => {
            const team = teams.find(t => t.id === entry.team_id);
            const isCurrent = entry.pick_number === currentPick;
            const isPicked = entry.pick_number < currentPick;
            const isSkipped = skippedPickNumbers.has(entry.pick_number);
            const { hasPickSkip, hasGuessWho, hasHalfTime } = getTeamSabotages(entry.team_id);
            const isMe = entry.team_id === myTeamId;

            // For Guess Who: show ??? for other teams, real name for self
            const displayName = (hasGuessWho && !isMe)
              ? '???'
              : (team?.name || 'Unknown');
            const shortName = displayName.length > 12
              ? displayName.slice(0, 11) + '…'
              : displayName;

            return (
              <div
                key={entry.pick_number}
                data-active={isCurrent ? 'true' : undefined}
                className={cn(
                  "flex flex-col items-center justify-center rounded-lg border transition-all duration-500",
                  isCurrent
                    ? "min-w-[160px] px-4 py-3 border-2"
                    : "min-w-[140px] px-3 py-2.5",
                  isCurrent && !hasHalfTime && !isPaused && !overnightPauseActive && [
                    "border-orange-500 bg-orange-500/15 shadow-[0_0_16px_rgba(249,115,22,0.3)]",
                    isCritical && "border-red-500 bg-red-500/15 shadow-[0_0_16px_rgba(239,68,68,0.35)] animate-pulse",
                  ],
                  isCurrent && hasHalfTime && !isPaused && !overnightPauseActive && [
                    "border-amber-500 bg-amber-500/15 shadow-[0_0_16px_rgba(245,158,11,0.3)]",
                    isCritical && "border-red-500 bg-red-500/15 shadow-[0_0_16px_rgba(239,68,68,0.35)] animate-pulse",
                  ],
                  isCurrent && (isPaused || overnightPauseActive) &&
                    "border-muted-foreground/30 bg-muted/30",
                  isPicked && !isSkipped && "opacity-40 bg-muted/20 border-transparent",
                  isSkipped && "opacity-60 bg-red-500/10 border-red-500/30 border-dashed",
                  !isCurrent && !isPicked && !isSkipped && "border-border/40 bg-background/50",
                  hasPickSkip && !isPicked && !isSkipped && "border-red-500/40",
                )}
              >
                {/* Team name + indicators */}
                <div className="flex items-center gap-1 w-full justify-center">
                  {isPicked && !isSkipped && (
                    <Check className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
                  )}
                  {isSkipped && (
                    <span className="text-sm shrink-0">💣</span>
                  )}
                  {hasPickSkip && !isPicked && !isSkipped && (
                    <span className="text-sm shrink-0">💣</span>
                  )}
                  {hasGuessWho && !isMe && (
                    <span className="text-sm shrink-0">👀</span>
                  )}
                  <span className={cn(
                    "font-semibold truncate leading-tight",
                    isCurrent ? "text-sm" : "text-[13px]",
                    isCurrent && "text-orange-300",
                    isCurrent && hasHalfTime && "text-amber-300",
                    isPicked && !isSkipped && "text-muted-foreground/60 line-through",
                    isSkipped && "text-red-400/70 line-through",
                  )}>
                    {isSkipped ? 'Skipped' : shortName}
                  </span>
                </div>

                {/* Pick number */}
                <span className={cn(
                  "text-[10px] text-muted-foreground/50 leading-tight mt-0.5",
                  isCurrent && "text-orange-400/70",
                )}>
                  Pick #{entry.pick_number}
                </span>

                {/* Timer inside active box */}
                {isCurrent && (
                  <div className="mt-1.5 flex items-center gap-1">
                    {overnightPauseActive ? (
                      <span className="text-xs text-muted-foreground">
                        🌙 Resumes {formatPauseEnd()}
                      </span>
                    ) : isPaused ? (
                      <span className="text-sm text-muted-foreground font-mono font-bold">PAUSED</span>
                    ) : (
                      <>
                        {/* Slow draft stage indicator */}
                        {isSlowDraft && stageIdx >= 0 && (
                          <span className="text-sm">{STAGE_ICONS[stageIdx]}</span>
                        )}
                        {hasHalfTime && (
                          <span className="text-sm">⏱️</span>
                        )}
                        <span className={cn(
                          "font-mono font-bold leading-tight",
                          isSlowDraft ? "text-base" : "text-lg",
                          isCritical ? "text-red-400" : isLowTime ? "text-yellow-400" : "text-orange-300",
                          hasHalfTime && !isCritical && !isLowTime && "text-amber-300",
                        )}>
                          {formatTime(timeRemaining)}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DraftOrderStrip;
